import { MessageDescriptor } from 'react-intl';
import { NavCardMessages } from './intl/NavCardMessages';
import StoneAgeIcon from './assets/icons/stoneage-logo-fill.svg';
import JettingCalculatorIcon from './assets/icons/calculate-fill.svg';
// TODO replace this PNG image with an SVG image [SSB-549].
import warthogSewerToolsTileIcon from './assets/images/icon-warthog_sewer_tools.png';
import { construct, school } from 'ionicons/icons';
import { Permission, Role } from './utils/types';
import { JobMessages } from './intl/JobMessages';
import { BundleMessages } from './intl/BundleMessages';
import { AppRouteConstants } from './pages/app-layout/AppRouteConstants';

export interface ResourceLink {
  actionUrl?: string;
  icon: string;
  iconIsPng?: boolean; // TODO remove this boolean when PNG has been replaced with SVG [SSB-549].
  title: MessageDescriptor;
  subtitle: MessageDescriptor;
  automationId?: string;
}

export interface SupportLink {
  actionUrl?: string;
  buttonText: MessageDescriptor;
  title: MessageDescriptor;
  subtitle: MessageDescriptor;
  automationId?: string;
}

export interface EntityPermission {
  type: 'org';
  principal: string;
  principalName: string;
  permissions: {
    readDetails?: boolean;
    readTimestamps?: boolean;
    readAnnotations?: boolean;
    readTubes?: boolean;
    readPressures?: boolean;
    readPies?: boolean;
    readConfiguration?: boolean;
    readIdleVsPressure?: boolean;
  };
}

// Resource links
// Broken down to individual link objects so they can be used to create arrays in multiple places.
export const yourAccountLink: ResourceLink = {
  title: NavCardMessages.yourAccountTitle,
  subtitle: NavCardMessages.yourAccountSubtitle,
  icon: StoneAgeIcon,
  actionUrl: `${process.env.REACT_APP_OPTIMIZELY_UI_URL}/MyAccount`,
  automationId: 'yourAccount',
};

export const waterblastToolsLink: ResourceLink = {
  title: NavCardMessages.waterblastToolsTitle,
  subtitle: NavCardMessages.waterblastToolsTitle,
  icon: StoneAgeIcon,
  actionUrl: 'https://www.stoneagetools.com/Catalog/products',
  automationId: 'waterblastTools',
};

export const warthogSewerToolsLink: ResourceLink = {
  title: NavCardMessages.warthogSewerToolsTitle,
  subtitle: NavCardMessages.warthogSewerToolsSubtitle,
  icon: warthogSewerToolsTileIcon,
  iconIsPng: true,
  actionUrl: 'https://www.warthog-nozzles.com',
  automationId: 'warthogSewerTools',
};

export const jettingCalculatorLink: ResourceLink = {
  title: NavCardMessages.jettingCalculatorTitle,
  subtitle: NavCardMessages.jettingCalculatorSubtitle,
  icon: JettingCalculatorIcon,
  actionUrl: 'http://jetting.stoneagetools.com/#/',
  automationId: 'jettingCalculator',
};

export const productInformationLink: ResourceLink = {
  title: NavCardMessages.productInformationTitle,
  subtitle: NavCardMessages.productInformationSubtitle,
  icon: construct,
  actionUrl:
    'https://www.stoneagetools.com/resource-center/ProductInformation?doFacetedSearches=true&onlyShowActiveDocuments=true&onlyShowDiscontinuedDocuments=false&page=1&pageSize=9&sortBy=3&query=&productLines=AutoBox%201L%20with%20Compass%20and%20Sentinel%20Technology%2CAutoBox%203L%20with%20Sentinel%20Technology',
  automationId: 'productInformation',
};

export const trainingLink: ResourceLink = {
  title: NavCardMessages.stoneAgeTrainingTitle,
  subtitle: NavCardMessages.stoneAgeTrainingSubtitle,
  icon: school,
  actionUrl:
    'https://www.stoneagetools.com/resource-center/ProductInformation?doFacetedSearches=true&onlyShowActiveDocuments=true&onlyShowDiscontinuedDocuments=false&page=1&pageSize=40&sortBy=3&query=&documentTypes=Training',
  automationId: 'stoneAgeTraining',
};

// Support links
export const feedbackLink: SupportLink = {
  title: NavCardMessages.supportFeedbackTitle,
  subtitle: NavCardMessages.supportFeedbackSubtitle,
  buttonText: NavCardMessages.supportFeedbackButtonLabel,
  actionUrl: '',
  automationId: 'provideFeedback',
};

export const contactLink: SupportLink = {
  title: NavCardMessages.supportContactStoneAgeTitle,
  subtitle: NavCardMessages.supportContactStoneAgeSubtitle,
  buttonText: NavCardMessages.supportContactStoneAgeTitle,
  actionUrl: 'https://www.stoneagetools.com/support',
  automationId: 'contactStoneAge',
};

export const JobsLinks: ResourceLink[] = [
  {
    title: JobMessages.logsTitle,
    subtitle: JobMessages.logsTitle,
    icon: '',
    actionUrl: AppRouteConstants.LogsList,
    automationId: 'jobsMenu',
  },
  {
    title: BundleMessages.bundlesTitle,
    subtitle: BundleMessages.bundlesTitle,
    icon: '',
    actionUrl: AppRouteConstants.BundlesList,
    automationId: 'bundlesMenu',
  },
];

export const RoleMap: Record<number, Role> = {
  10: 'USER',
  20: 'MANAGER',
  30: 'ADMIN',
  40: 'STONEAGE_ADMIN',
  50: 'STONEAGE_DEV',
};

export const PermissionMap: Record<Role, Permission[]> = {
  USER: ['UploadJobLog', 'UpdateFirmware', 'ViewJobLogs', 'ViewControllers', 'ViewBundles'],
  MANAGER: [
    'UploadJobLog',
    'UpdateFirmware',
    'ViewJobLogs',
    'ShareJobLog',
    'ViewControllers',
    'ViewNonConnectedControllers',
    'ViewBundles',
    'ShareBundle',
  ],
  ADMIN: [
    'UploadJobLog',
    'UpdateFirmware',
    'ViewJobLogs',
    'ShareJobLog',
    'ViewControllers',
    'ViewNonConnectedControllers',
    'ViewBundles',
    'ShareBundle',
  ],
  STONEAGE_ADMIN: [
    'UploadJobLog',
    'ViewJobLogs',
    'ShareJobLog',
    'ViewControllers',
    'ViewNonConnectedControllers',
    'ViewBundles',
    'ViewOrganizationSubscriptions',
    'ShareBundle',
  ],
  STONEAGE_DEV: [
    'UploadJobLog',
    'UpdateFirmware',
    'ViewJobLogs',
    'ShareJobLog',
    'ViewControllers',
    'ViewNonConnectedControllers',
    'ViewFirmware',
    'ViewStoneAgeUsers',
    'ViewOrganizationSubscriptions',
    'ViewBundles',
    'ShareBundle',
  ],
};
